/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import React, { useEffect, useState, useRef } from "react";
import { Drawer } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import CloseIcon from "../../../../Common/Svgs/CloseIcon";
import { client } from "../../../../Utils/axiosClient";

const Star = ({ filled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    fill={filled ? "#FFD700" : "none"}
    stroke="none"
    viewBox="0 0 18 18"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m10.274 1.694 1.467 2.933c.2.408.733.8 1.183.875l2.658.442c1.7.283 2.1 1.516.875 2.733l-2.066 2.067c-.35.35-.542 1.025-.434 1.508l.592 2.558c.467 2.025-.608 2.809-2.4 1.75l-2.492-1.475c-.45-.266-1.191-.266-1.65 0L5.516 16.56c-1.784 1.059-2.867.267-2.4-1.75l.591-2.558c.109-.483-.083-1.158-.433-1.508L1.207 8.677c-1.216-1.217-.825-2.45.875-2.733l2.659-.442c.441-.075.975-.467 1.175-.875l1.466-2.933c.8-1.592 2.1-1.592 2.892 0"
    />
  </svg>
);

const FeedbackCard = ({ item }) => (
  <div className="mt-4 rounded-xl p-4 bg-background">
    <div className="flex justify-between items-start">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} filled={n <= item?.rating} />
        ))}
      </div>

      <p className="text-xs font-aileron font-semibold">
        {dayjs(item?.createdAt).format("DD-MM-YYYY")}
      </p>
    </div>

    {item?.comment ? (
      <>
        <p className="mt-2 font-aileron text-xs text-dark-gray leading-5">
          {item?.comment}
        </p>

        <div className="w-full h-px bg-[#E4E7EC] my-2" />
      </>
    ) : null}

    <div className="mt-3">
      <div className="flex justify-between">
        <p className="text-xs font-aileron text-dark-gray font-normal">
          Added By
        </p>
        <p className="text-xs font-aileron font-semibold">
          {item?.createdBy || "--"}
        </p>
      </div>

      {item?.tripId != null && (
        <div className="flex justify-between">
          <p className="text-xs font-aileron text-dark-gray font-normal">
            Trip ID
          </p>
          <p className="text-xs font-aileron font-semibold">{item?.tripId}</p>
        </div>
      )}
    </div>
  </div>
);

const DriverFeedbackDrawer = ({ visible, onCancel }) => {
  const drawerData = useSelector((state) => state.users.driverFeedbackDrawer);
  const driverId = drawerData?.driverId;

  const [feedbacks, setFeedbacks] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const scrollRef = useRef();
  const sentinelRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loading) {
          setPage((p) => p + 1);
        }
      },
      {
        root: scrollRef.current,
        threshold: 1.0,
      },
    );

    if (visible && sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [visible, hasMore, loading]);

  const loadMore = async () => {
    if (loading || !hasMore) return;

    setLoading(true);

    try {
      const res = await client.get(
        `${process.env.REACT_APP_API_URL}/drivers/get-feedback`,
        {
          params: {
            driverId,
            page,
            limit: 10,
          },
        },
      );

      if (page === 1) {
        setFeedbacks(res.data.feedbacks || []);
      } else {
        setFeedbacks((prev) => [...prev, ...res.data.feedbacks]);
      }

      setHasMore(res.data.hasMore);
      setLoading(false);
    } catch (error) {
      console.log("error fetching feedbacks ", error);
    }
  };

  useEffect(() => {
    if (driverId && visible) {
      setPage(1);
      setHasMore(true);
      setFeedbacks([]);
      setLoading(false);
      loadMore();
    }
  }, [driverId, visible]);

  useEffect(() => {
    if (page > 1 && driverId) loadMore();
  }, [page]);

  return (
    <Drawer
      width={560}
      placement="right"
      closable={false}
      zIndex={2000}
      maskStyle={{
        background: "transparent",
      }}
      open={visible}
      onClose={() => {
        onCancel();
        setPage(1);
        setFeedbacks([]);
        setHasMore(true);
      }}
      className="rounded-tl-xl"
    >
      <div className="p-6 bg-background-white h-full overflow-y-auto">
        <div className="flex items-center justify-between">
          <p className="font-poppins font-medium text-base">
            Historical Feedback
          </p>
          <CloseIcon
            className="w-5 h-5 cursor-pointer stroke-dark-grey-text"
            onClick={() => {
              onCancel();
              setPage(1);
              setFeedbacks([]);
              setHasMore(true);
            }}
          />
        </div>

        <div className="flex items-center gap-2 mt-3 bg-[#F9F4E2] w-fit px-2 py-1 rounded-lg">
          <Star filled />
          <p className="font-poppins font-semibold text-2xl">
            {drawerData?.avgRating}
          </p>
        </div>

        {feedbacks.length > 0 && (
          <>
            <p className="mt-5 font-poppins font-medium text-base">Latest</p>
            <FeedbackCard item={feedbacks[0]} />
          </>
        )}
        {feedbacks.length > 1 && (
          <>
            <p className="mt-6 font-poppins font-medium text-base">Older</p>

            {feedbacks.slice(1)?.map((item) => (
              <FeedbackCard item={item} />
            ))}

            {loading && <p className="text-center mt-4">Loading...</p>}
          </>
        )}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </Drawer>
  );
};

export default DriverFeedbackDrawer;
